/**
 * PURE. Creature mesh pipeline (creatures §B10): resolveSpec → buildRig → parts (head, body, garb) → budget pass
 * (dressed ≤ 2,230 tris; art §5.2) → pack into the art §3.2 layouts (24 B render, 20 B hull) normalized to H.
 * Identical code + DNA → bit-identical buffers (art §5.1).
 */
import { CREATURE_VERTEX_BYTES, HULL_VERTEX_BYTES, MAX_CREATURE_TRIS, type CreatureDNA, type CreatureMeshData, type PointName } from '../../shared';
import { resolveSpec, type CreatureSpec } from '../dna/resolve';
import { buildRig, type BuiltRig, type RigOptions } from '../rig/skeleton';
import { buildBody } from './body';
import { MeshBuilder, type PartRange, type V3 } from './builder';
import { LOD_CFG, MAT, type BuildCtx, type Lod, type LodCfg } from './ctx';
import { buildGarb } from './garb';
import { buildHead } from './head';

export interface BuildOpts extends RigOptions {
  lod?: Lod;
  /** Skip garb (portrait "bare" previews, tests). */
  bare?: boolean;
  /** Hide the idle prop (battle: the weapon takes the paw, creatures §B7.4). */
  battle?: boolean;
}

export interface BuiltCreature {
  readonly spec: CreatureSpec;
  readonly built: BuiltRig;
  readonly mesh: CreatureMeshData;
  /** Per-part render/hull ranges (drag preview, creatures §A4.3). */
  readonly parts: readonly PartRange[];
  /** Budget reductions applied by the budget pass. */
  readonly reductions: readonly string[];
}

const BIRDS = new Set(['goose', 'owl']);

function makeCtx(spec: CreatureSpec, built: BuiltRig, lod: Lod, cfg: LodCfg): BuildCtx {
  const index = built.rig.index;
  return {
    spec, rig: built, P: built.raw, d: built.dims, lod, cfg, mb: new MeshBuilder(),
    bi: (n) => { const i = index[n]; if (i === undefined) throw new Error(`bone ${n} missing`); return i; },
    ob: (n) => index[n] ?? -1,
    headMat: BIRDS.has(spec.head.id) ? MAT.plumage : MAT.fur,
    bodyMat: BIRDS.has(spec.body.id) ? MAT.plumage : MAT.fur,
    face: built.faceInfo.base,
    seed: (spec.dna.soulSeed % 997) + 17,
  };
}

/** Budget reductions in creatures §B7.4 order: cape, garment skirt, tail, torso stagger. */
const REDUCTIONS: Array<[string, (c: LodCfg) => LodCfg]> = [
  ['cape', (c) => ({ ...c, clothRows: Math.max(2, c.clothRows - 2), clothCols: Math.max(2, c.clothCols - 1) })],
  ['skirt', (c) => ({ ...c, clothCols: 1 })],
  ['tail', (c) => ({ ...c, tailRings: Math.max(5, c.tailRings - 4), tailSides: 4 })],
  ['torso', (c) => ({ ...c, torsoSides: 6, headSides: Math.min(c.headSides, 9) })],
  ['limbs', (c) => ({ ...c, limbSides: 5, limbRings: 3 })],
];

/** Builds the mesh of a resolved spec on an existing rig. */
export function buildCreatureMesh(spec: CreatureSpec, built: BuiltRig, opts: BuildOpts = {}): { mesh: CreatureMeshData; parts: PartRange[]; reductions: string[] } {
  const lod = opts.lod ?? 0;
  let cfg = LOD_CFG[String(lod) as '0' | '1' | 'hero'];
  const reductions: string[] = [];
  let mb: MeshBuilder;
  for (let attempt = 0; ; attempt++) {
    const ctx = makeCtx(spec, built, lod, cfg);
    buildHead(ctx);
    buildBody(ctx);
    if (!opts.bare) buildGarb(ctx, { battle: !!opts.battle });
    mb = ctx.mb;
    if (mb.renderTris <= MAX_CREATURE_TRIS || attempt >= REDUCTIONS.length) break;
    const [name, fn] = REDUCTIONS[attempt];
    reductions.push(name);
    cfg = fn(cfg);
  }
  return { mesh: pack(mb, built, lod), parts: mb.parts, reductions };
}

/** DNA → spec → rig → mesh. */
export function buildCreature(dna: CreatureDNA, opts: BuildOpts = {}): BuiltCreature {
  const spec = resolveSpec(dna);
  const built = buildRig(spec, opts);
  const { mesh, parts, reductions } = buildCreatureMesh(spec, built, opts);
  return { spec, built, mesh, parts, reductions };
}

const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

/** Packs the builder into the art §3.2 layouts (positions normalized to H). */
function pack(mb: MeshBuilder, built: BuiltRig, lod: Lod): CreatureMeshData {
  const s = built.norm;
  const nV = mb.rPos.length / 3;
  const vb = new ArrayBuffer(nV * CREATURE_VERTEX_BYTES);
  const dv = new DataView(vb);
  const min: V3 = [Infinity, Infinity, Infinity], max: V3 = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < nV; i++) {
    const o = i * CREATURE_VERTEX_BYTES;
    for (let k = 0; k < 3; k++) {
      const v = Math.fround(mb.rPos[i * 3 + k] * s);
      dv.setFloat32(o + k * 4, v, true);
      if (v < min[k]) min[k] = v; if (v > max[k]) max[k] = v;
    }
    dv.setUint16(o + 12, Math.round(clamp01((mb.rU[i] * s) / 2) * 65535), true);
    dv.setUint16(o + 14, Math.round(clamp01(mb.rV[i]) * 65535), true);
    const bA = mb.rSkin[i * 3], bB = mb.rSkin[i * 3 + 1], wA = mb.rSkin[i * 3 + 2];
    dv.setUint8(o + 16, bA); dv.setUint8(o + 17, bB); dv.setUint8(o + 18, Math.round(clamp01(wA) * 255));
    dv.setUint8(o + 19, Math.round(Math.max(0, Math.min(2, mb.rLw[i])) * 127.5));
    dv.setUint8(o + 20, mb.rInfo[i * 4]); dv.setUint8(o + 21, mb.rInfo[i * 4 + 1]); dv.setUint8(o + 22, mb.rInfo[i * 4 + 2]); dv.setUint8(o + 23, mb.rInfo[i * 4 + 3]);
  }
  const nH = mb.hPos.length / 3;
  const hb = new ArrayBuffer(nH * HULL_VERTEX_BYTES);
  const hv = new DataView(hb);
  const sn = (x: number): number => Math.max(-127, Math.min(127, Math.round(x * 127)));
  for (let i = 0; i < nH; i++) {
    const o = i * HULL_VERTEX_BYTES;
    for (let k = 0; k < 3; k++) hv.setFloat32(o + k * 4, Math.fround(mb.hPos[i * 3 + k] * s), true);
    hv.setInt8(o + 12, sn(mb.hNrm[i * 3])); hv.setInt8(o + 13, sn(mb.hNrm[i * 3 + 1])); hv.setInt8(o + 14, sn(mb.hNrm[i * 3 + 2]));
    hv.setInt8(o + 15, sn(Math.max(0, Math.min(1, mb.hLw[i] / 2))));
    hv.setUint8(o + 16, mb.hSkin[i * 4]); hv.setUint8(o + 17, mb.hSkin[i * 4 + 1]); hv.setUint8(o + 18, Math.round(clamp01(mb.hSkin[i * 4 + 2]) * 255));
    hv.setUint8(o + 19, mb.hSkin[i * 4 + 3]);
  }
  const indices = new Uint16Array(mb.hIdx);
  const P = built.pivots;
  const idx = built.rig.index;
  const pt = (bone: keyof typeof idx, pos: V3 | undefined): { bone: number; pos: [number, number, number] } | undefined =>
    pos && idx[bone] !== undefined ? { bone: idx[bone]!, pos: [pos[0], pos[1], pos[2]] } : undefined;
  const eyesMid: V3 | undefined = P.eye_L && P.eye_R ? [(P.eye_L[0] + P.eye_R[0]) / 2, P.eye_L[1], P.eye_L[2]] : undefined;
  const tailTip = built.tailPath.length ? built.tailPath[built.tailPath.length - 1] : undefined;
  const lastTail = built.chains.tail.length ? built.chains.tail[built.chains.tail.length - 1] : idx.pelvis!;
  const points: Partial<Record<PointName, { bone: number; pos: [number, number, number] }>> = {
    headTop: pt('head', [0, 1, P.head![2]]), eyes: pt('head', eyesMid), mouth: pt('mouth', P.mouth), chest: pt('spine_02', P.spine_02 && [0, P.spine_02[1] + 0.03, 0.08]),
    pelvis: pt('pelvis', P.pelvis), handL: pt('hand_L', P.hand_L), handR: pt('hand_R', P.hand_R), footL: pt('toe_L', P.toe_L), footR: pt('toe_R', P.toe_R),
    tailTip: tailTip ? { bone: lastTail, pos: [tailTip[0], tailTip[1], tailTip[2]] } : undefined,
  };
  for (const k of Object.keys(points) as PointName[]) if (!points[k]) delete points[k];
  return {
    vertices: vb, vertexCount: nV,
    hull: { vertices: hb, indices, vertexCount: nH, indexCount: indices.length },
    bounds: { min: [min[0], min[1], min[2]], max: [max[0], max[1], max[2]] },
    points, lod, tris: { render: nV / 3, hull: indices.length / 3 },
  };
}

/** Decoded render vertex (tests, previews, QA). */
export interface RenderVertex { pos: V3; u: number; v: number; boneA: number; boneB: number; weightA: number; lineWeight: number; material: number; region: number; edgeMask: number; corner: number }
export function readRenderVertex(mesh: CreatureMeshData, i: number): RenderVertex {
  const dv = new DataView(mesh.vertices);
  const o = i * CREATURE_VERTEX_BYTES;
  return {
    pos: [dv.getFloat32(o, true), dv.getFloat32(o + 4, true), dv.getFloat32(o + 8, true)],
    u: (dv.getUint16(o + 12, true) / 65535) * 2, v: dv.getUint16(o + 14, true) / 65535,
    boneA: dv.getUint8(o + 16), boneB: dv.getUint8(o + 17), weightA: dv.getUint8(o + 18), lineWeight: dv.getUint8(o + 19) / 127.5,
    material: dv.getUint8(o + 20), region: dv.getUint8(o + 21), edgeMask: dv.getUint8(o + 22), corner: dv.getUint8(o + 23),
  };
}
